import { AxiosResponse } from 'axios'
import IMessageAndPackApiResponse from 'src/system/content/api/IMessageAndPackApiResponse'
import IMessageAndRecipientApiResponse from 'src/system/content/api/IMessageAndRecipientApiResponse'
import { IMessageApiResponse } from 'src/system/content/api/IMessageApiResponse'
import { IMessageRecipientApiResponse } from 'src/system/content/api/IMessageRecipientApiResponse'
import { IPackApiResponse } from 'src/system/content/api/IPackApiResponse'
import IPackFooterApiResponse from 'src/system/content/api/IPackFooterApiResponse'
import IPackHeaderApiResponse from 'src/system/content/api/IPackHeaderApiResponse'
import IPackSendApiRequest from 'src/system/content/api/IPackSendApiRequest'
import { LogDocType } from 'src/system/content/schemas/log'
import { MessageDocType, MessageDocument } from 'src/system/content/schemas/message'
import { MessageRecipientDocType } from 'src/system/content/schemas/messageRecipient'
import { PackDocType } from 'src/system/content/schemas/pack'
import { PackFooterDocType } from 'src/system/content/schemas/packFooter'
import { PackHeaderDocType } from 'src/system/content/schemas/packHeader'
import { ResourceDocType } from 'src/system/content/schemas/resource'
import { SignatureDocType } from 'src/system/content/schemas/signature'
import ILocalContentService from 'src/system/content/services/ILocalContentService'
import IPackService from 'src/system/content/services/IPackService'
import IRemoteContentService from 'src/system/content/services/IRemoteContentService'
import { QuoteDocType } from 'src/system/content/schemas/quote'

export class PackService implements IPackService {
  constructor(private localContentService: ILocalContentService, private remoteContentService: IRemoteContentService) {
    //
  }

  async fetchAllPacks(): Promise<void> {
    const response: AxiosResponse<IPackApiResponse[]> = await this.remoteContentService.request('GET', '/api/pack')

    const responsePacks = response.data.map((pack) => ({
      id: pack.id.toString(),
      title: pack.title,
      version: pack.version,
      user: pack.user_id.toString(),
      notes: pack.notes,
      colour: pack.colour,
      header: pack.header_id.toString(),
      footer: pack.footer_id.toString(),
      contents: JSON.stringify(pack.contents),
      lastSent: pack.last_sent,
      sendCount: pack.send_count,
      quoteValue: pack.quote_value,
      isPasswordProtected: pack.is_password_protected,
      defaultEmailSubject: pack.meta?.email?.defaultSubject,
      defaultEmailGreeting: pack.meta?.email?.defaultGreeting,
      defaultEmailMessage: pack.meta?.email?.defaultMessage,
      defaultEmailLinkType: pack.meta?.email?.defaultLinkType,
      defaultEmailLinkText: pack.meta?.email?.defaultLinkText,
    }))

    const db = this.localContentService.getDatabase()

    const storedIds = (await db.packs.find().exec()).map((pack) => pack.id)
    const responseIds = responsePacks.map((pack) => pack.id)

    const idsToDelete = storedIds.filter((id) => !responseIds.includes(id))

    await Promise.all([db.packs.bulkRemove(idsToDelete), db.packs.bulkUpsert(responsePacks)])
  }

  async fetchAllMessages(): Promise<void> {
    const response: AxiosResponse<IMessageApiResponse[]> = await this.remoteContentService.request(
      'GET',
      '/api/message'
    )

    const responseMessages = response.data.map((message) => ({
      id: message.id.toString(),
      uuid: message.uuid,
      pack: message.pack_id.toString(),
      recipient: message.recipient_id.toString(),
      status: message.status,
      user: message.user_id.toString(),
      sentAt: message.sent_at,
      emailSubject: message.email_subject,
      emailGreeting: message.email_greeting,
      emailValue: message.email_value,
      emailLinkType: message.email_link_type,
      emailLinkText: message.email_link_text,
      value: message.value,
      packVisits: message.pack_visits,
      downloads: message.downloads,
      firstVisit: message.first_visit,
      lastVisit: message.last_visit,
      signature: message.signature_id.toString(),
    }))

    const responseLogs: LogDocType[] = []

    response.data.forEach((message) => {
      message.logs.forEach(
        (log) =>
          void responseLogs.push({
            id: log.id.toString(),
            name: log.log_type.name,
            pack: log.data.pack?.id?.toString() || null,
            message: log.data.message?.id?.toString() || null,
            resourceName: log.data.resource?.name?.toString() || null,
            createdAt: log.created_at,
            updatedAt: log.updated_at,
          })
      )
    })

    const db = this.localContentService.getDatabase()

    const storedMessageIds = (await db.messages.find().exec()).map((message) => message.id)
    const responseMessageIds = responseMessages.map((message) => message.id)

    const messageIdsToDelete = storedMessageIds.filter((id) => !responseMessageIds.includes(id))

    const storedLogIds = (await db.logs.find().exec()).map((log) => log.id)
    const responseLogIds = responseLogs.map((log) => log.id)

    const logIdsToDelete = storedLogIds.filter((id) => !responseLogIds.includes(id))

    await Promise.all([
      db.messages.bulkRemove(messageIdsToDelete),
      db.messages.bulkUpsert(responseMessages),
      db.logs.bulkRemove(logIdsToDelete),
      db.logs.bulkUpsert(responseLogs),
    ])
  }

  async fetchMessageWithAssociatedEntities(uuid: string, password?: string): Promise<void> {
    const response: AxiosResponse<IMessageAndPackApiResponse> = await this.remoteContentService.request(
      'POST',
      `/api/message/uuid/${uuid}`,
      { password: password || null }
    )

    const messageData = response.data.message
    const packData = response.data.pack
    const headerData = response.data.header
    const footerData = response.data.footer
    const signatureData = response.data.signature

    const responseMessage: MessageDocType = {
      id: messageData.id.toString(),
      uuid: messageData.uuid,
      pack: messageData.pack_id.toString(),
      recipient: messageData.recipient_id.toString(),
      status: messageData.status,
      user: messageData.user_id.toString(),
      sentAt: messageData.sent_at,
      emailSubject: messageData.email_subject,
      emailGreeting: messageData.email_greeting,
      emailValue: messageData.email_value,
      emailLinkType: messageData.email_link_type,
      emailLinkText: messageData.email_link_text,
      value: messageData.value,
      packVisits: messageData.pack_visits,
      downloads: messageData.downloads,
      firstVisit: messageData.first_visit,
      lastVisit: messageData.last_visit,
      signature: messageData.signature_id.toString(),
    }

    const responsePack: PackDocType = {
      id: packData.id.toString(),
      title: packData.title,
      version: packData.version,
      user: packData.user_id.toString(),
      notes: packData.notes,
      colour: packData.colour,
      header: packData.header_id.toString(),
      footer: packData.footer_id.toString(),
      contents: JSON.stringify(packData.contents),
      lastSent: packData.last_sent,
      sendCount: packData.send_count,
      quoteValue: packData.quote_value,
      isPasswordProtected: packData.is_password_protected,
      defaultEmailSubject: packData.meta?.email?.defaultSubject,
      defaultEmailGreeting: packData.meta?.email?.defaultGreeting,
      defaultEmailMessage: packData.meta?.email?.defaultMessage,
      defaultEmailLinkType: packData.meta?.email?.defaultLinkType,
      defaultEmailLinkText: packData.meta?.email?.defaultLinkText,
    }

    const responseResources: ResourceDocType[] = response.data.resources.map((resource) => ({
      resourceId: resource.id.toString(),
      type: resource.type,
      folders: [],
      name: resource.name,
      description: resource.description,
      thumbnail: resource.thumbnail,
      image: resource.image,
      document: resource.document,
      url: resource.url,
      video: resource.video,
      fileSize: resource.file_size,
      createdAt: resource.created_at,
      updatedAt: resource.updated_at,
    }))

    const responseQuotes: QuoteDocType[] = response.data.quotes.map((quote) => ({
      id: quote.id.toString(),
      title: quote.title,
      introduction: quote.introduction,
      validUntil: quote.valid_until,
      resources: quote.resources?.map((resource) => `${resource.type}-${resource.id}`) || [],
      data: quote.data,
      value: quote.value,
      createdAt: quote.created_at,
      updatedAt: quote.updated_at,
    }))

    const responseHeader: PackHeaderDocType = {
      id: headerData.id.toString(),
      name: headerData.name,
      image: headerData.image,
    }

    const responseFooter: PackFooterDocType = {
      id: footerData.id.toString(),
      name: footerData.name,
      image: footerData.image,
    }

    const responseSignature: SignatureDocType = {
      id: signatureData.id.toString(),
      user: signatureData.user_id.toString(),
      name: signatureData.name,
      title: signatureData.title,
      telephone: signatureData.telephone,
      email: signatureData.email,
      image: signatureData.image,
      linkedin: signatureData.linkedin,
      bio: signatureData.bio,
      specialisms: signatureData.specialisms,
    }

    const db = this.localContentService.getDatabase()

    await Promise.all([
      db.messages.upsert(responseMessage),
      db.packs.upsert(responsePack),
      db.resources.bulkUpsert(responseResources),
      db.quotes.bulkUpsert(responseQuotes),
      db.packHeaders.upsert(responseHeader),
      db.packFooters.upsert(responseFooter),
      db.signatures.upsert(responseSignature),
    ])
  }

  async fetchAllMessageRecipients(): Promise<void> {
    const response: AxiosResponse<IMessageRecipientApiResponse[]> = await this.remoteContentService.request(
      'GET',
      '/api/message/recipients'
    )

    const responseRecipients = response.data.map((recipient) => ({
      id: recipient.id.toString(),
      email: recipient.email,
      firstName: recipient.first_name,
      lastName: recipient.last_name,
      company: recipient.company,
    }))

    const db = this.localContentService.getDatabase()

    const storedIds = (await db.messageRecipients.find().exec()).map((recipient) => recipient.id)
    const responseIds = responseRecipients.map((recipient) => recipient.id)

    const idsToDelete = storedIds.filter((id) => !responseIds.includes(id))

    await Promise.all([
      db.messageRecipients.bulkRemove(idsToDelete),
      db.messageRecipients.bulkUpsert(responseRecipients),
    ])
  }

  async fetchAllHeaders(): Promise<void> {
    const response: AxiosResponse<IPackHeaderApiResponse[]> = await this.remoteContentService.request(
      'GET',
      '/api/pack/header'
    )

    const responseHeaders = response.data.map((header) => ({
      id: header.id.toString(),
      name: header.name,
      image: header.image,
    }))

    const db = this.localContentService.getDatabase()

    const storedIds = (await db.packHeaders.find().exec()).map((header) => header.id)
    const responseIds = responseHeaders.map((header) => header.id)

    const idsToDelete = storedIds.filter((id) => !responseIds.includes(id))

    await Promise.all([db.packHeaders.bulkRemove(idsToDelete), db.packHeaders.bulkUpsert(responseHeaders)])
  }

  async fetchAllFooters(): Promise<void> {
    const response: AxiosResponse<IPackFooterApiResponse[]> = await this.remoteContentService.request(
      'GET',
      '/api/pack/footer'
    )

    const responseFooters = response.data.map((footer) => ({
      id: footer.id.toString(),
      name: footer.name,
      image: footer.image,
    }))

    const db = this.localContentService.getDatabase()

    const storedIds = (await db.packFooters.find().exec()).map((footer) => footer.id)
    const responseIds = responseFooters.map((footer) => footer.id)

    const idsToDelete = storedIds.filter((id) => !responseIds.includes(id))

    await Promise.all([db.packFooters.bulkRemove(idsToDelete), db.packFooters.bulkUpsert(responseFooters)])
  }

  async savePack(data: IPackApiResponse): Promise<number | null> {
    const response = await this.remoteContentService.request('POST', '/api/pack/save', data)
    return typeof response.data.pack_id === 'number' ? response.data.pack_id : null
  }

  async sendPack(pack: PackDocType, data: IPackSendApiRequest): Promise<MessageDocument> {
    data.pack_id = pack.id

    const response: AxiosResponse<IMessageAndRecipientApiResponse> = await this.remoteContentService.request(
      'POST',
      '/api/pack/send',
      data
    )

    const responseMessage: MessageDocType = {
      id: response.data.id.toString(),
      uuid: response.data.uuid,
      pack: response.data.pack_id.toString(),
      recipient: response.data.recipient_id.toString(),
      status: response.data.status,
      user: response.data.user_id.toString(),
      sentAt: response.data.sent_at,
      emailSubject: response.data.email_subject,
      emailGreeting: response.data.email_greeting,
      emailValue: response.data.email_value,
      emailLinkType: response.data.email_link_type,
      emailLinkText: response.data.email_link_text,
      value: response.data.value,
      packVisits: response.data.pack_visits,
      downloads: response.data.downloads,
      firstVisit: response.data.first_visit,
      lastVisit: response.data.last_visit,
      signature: response.data.signature_id.toString(),
    }

    const responseRecipient: MessageRecipientDocType = {
      id: response.data.recipient.id.toString(),
      email: response.data.recipient.email,
      firstName: response.data.recipient.first_name,
      lastName: response.data.recipient.last_name,
      company: response.data.recipient.company || null,
    }

    const db = this.localContentService.getDatabase()

    const [document] = await Promise.all([
      db.messages.upsert(responseMessage),
      db.messageRecipients.upsert(responseRecipient),
    ])

    return document
  }

  async resendPack(messageId: string): Promise<void> {
    await this.remoteContentService.request('POST', '/api/pack/resend', { id: Number(messageId) })
  }
}
